import {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

import Box from "@/components/Box";
import { theme } from "@/core/theme";
import Group from "@/tpo/Group";
import Stack from "@/tpo/Stack";

import { cmpHomeDashboardGridGap, cmpHomeDashboardNarrowColumnWidth } from "./dashboardLayout";

export function useColumnCount() {
  const [columnCount, setColumnCount] = useState(1);

  useEffect(() => {
    const medium = window.matchMedia(`(min-width: ${theme.breakpoints[1]})`);
    const large = window.matchMedia(`(min-width: ${theme.breakpoints[2]})`);

    const update = () => {
      if (large.matches) {
        setColumnCount(3);
      } else if (medium.matches) {
        setColumnCount(2);
      } else {
        setColumnCount(1);
      }
    };

    update();
    medium.addEventListener("change", update);
    large.addEventListener("change", update);
    return () => {
      medium.removeEventListener("change", update);
      large.removeEventListener("change", update);
    };
  }, []);

  return columnCount;
}

/** Fill column 1 top-to-bottom, then column 2, etc. (see CmpHomeDashboard child order). */
function splitIntoColumns(items, columnCount) {
  if (columnCount <= 1) {
    return [items];
  }

  const columnCountUsed = Math.min(columnCount, items.length);
  const perColumn = Math.ceil(items.length / columnCountUsed);

  return Array.from({ length: columnCountUsed }, (_, columnIndex) =>
    items.slice(columnIndex * perColumn, (columnIndex + 1) * perColumn)
  );
}

function useNarrowColumnMaxHeight(enabled, measureColumnRefs, layoutKey) {
  const [maxHeight, setMaxHeight] = useState(undefined);

  useEffect(() => {
    if (!enabled) {
      setMaxHeight(undefined);
      return undefined;
    }

    let observer;
    let rafId;

    const measure = () => {
      const heights = measureColumnRefs
        .map(ref => ref.current)
        .filter(Boolean)
        .map(node => node.getBoundingClientRect().height);

      if (heights.length === measureColumnRefs.length) {
        setMaxHeight(Math.max(...heights));
      }
    };

    const attach = () => {
      const nodes = measureColumnRefs.map(ref => ref.current).filter(Boolean);
      if (nodes.length < measureColumnRefs.length) {
        rafId = requestAnimationFrame(attach);
        return;
      }

      measure();
      observer = new ResizeObserver(measure);
      nodes.forEach(node => observer.observe(node));
    };

    attach();
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [enabled, layoutKey, measureColumnRefs]);

  return maxHeight;
}

export function MasonryItem({ children, fill }) {
  return (
    <Box
      width="100%"
      {...(fill
        ? {
            flex: "1 1 0%",
            minHeight: 0,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }
        : {})}
    >
      {children}
    </Box>
  );
}

/**
 * Masonry-style dashboard layout. Native `grid-template-rows: masonry` is not
 * supported in Chrome/Firefox stable yet, so we use flex column stacks. On
 * desktop (3 columns), column 1 height is capped to the taller of columns 2–3;
 * News fills the space below Active discounts and scrolls inside the panel.
 */
export function Masonry({ children, ...containerProps }) {
  const columnCount = useColumnCount();
  const items = Children.toArray(children);

  const columns = useMemo(() => splitIntoColumns(items, columnCount), [items, columnCount]);

  const capNarrowColumn = columnCount === 3;
  const middleColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const measureColumnRefs = useMemo(() => [middleColumnRef, rightColumnRef], []);
  const narrowColumnMaxHeight = useNarrowColumnMaxHeight(
    capNarrowColumn,
    measureColumnRefs,
    columns
  );

  return (
    <Box {...containerProps}>
      <Group alignItems="start" width="100%" gap={cmpHomeDashboardGridGap}>
        {columns.map((columnItems, columnIndex) => {
          const isNarrowColumn = columnIndex === 0 && columns.length > 1;
          const isMiddleColumn = capNarrowColumn && columnIndex === 1;
          const isRightColumn = capNarrowColumn && columnIndex === 2;

          const narrowColumnCapped =
            isNarrowColumn && narrowColumnMaxHeight != null && capNarrowColumn;

          return (
            <Stack
              key={columnIndex}
              ref={isMiddleColumn ? middleColumnRef : isRightColumn ? rightColumnRef : undefined}
              gap={narrowColumnCapped ? undefined : cmpHomeDashboardGridGap}
              rowGap={narrowColumnCapped ? cmpHomeDashboardGridGap : undefined}
              display={narrowColumnCapped ? "grid" : undefined}
              gridTemplateRows={narrowColumnCapped ? "auto minmax(0, 1fr)" : undefined}
              flex={isNarrowColumn ? "0 0 auto" : "1 1 0"}
              minWidth={0}
              width={isNarrowColumn ? cmpHomeDashboardNarrowColumnWidth : "100%"}
              maxWidth={isNarrowColumn ? cmpHomeDashboardNarrowColumnWidth : "100%"}
              maxHeight={narrowColumnCapped ? narrowColumnMaxHeight : undefined}
              height={narrowColumnCapped ? narrowColumnMaxHeight : undefined}
              minHeight={narrowColumnCapped ? 0 : undefined}
              overflow={narrowColumnCapped ? "hidden" : undefined}
            >
              {columnItems.map(item => {
                const isNewsSlot = capNarrowColumn && columnIndex === 0 && item.key === "news";

                if (isNewsSlot) {
                  const newsItem = isValidElement(item) ? cloneElement(item, { fill: true }) : item;

                  return (
                    <Box
                      key={item.key}
                      width="100%"
                      minHeight={0}
                      overflow="hidden"
                      display="flex"
                      flexDirection="column"
                    >
                      {newsItem}
                    </Box>
                  );
                }

                return item;
              })}
            </Stack>
          );
        })}
      </Group>
    </Box>
  );
}
